import React, {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Organization,
  SalesTransaction,
  Applicant,
  Client,
  DailyPerformanceLog,
  SharedResource,
  ApplicantStatus,
  ClientStatus,
  ClientTask,
  ClientFile,
  ClientCommunication,
  AnalyticsUpload,
  LeaderboardEntry,
  TeamMember,
  PrestigeEntry,
  PrestigeRank,
} from "../types";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { useAuth } from "./AuthContext";
import { useRBAC } from "../hooks/useRBAC";
import { N8N_WEBHOOKS } from "../constants";
import { securelyTriggerN8n, triggerPublicWebhook } from "../lib/n8n";
import { logger } from "../lib/logger";

// =============================================================================
// DataContext - Orchestrator for Domain-Specific Contexts
// =============================================================================
// PURPOSE: Composes domain contexts (Transaction, Team, ClientPortal) and manages
// organization selection/switching. Provides backward-compatible useData() API.
//
// ARCHITECTURE:
// - Outer wrapper: DataProvider composes TransactionProvider, TeamProvider, ClientPortalProvider
// - Inner wrapper: DataProviderInternal provides unified useData() API
// - Organization state managed here and passed to domain providers
// - RBAC filtering applied to transactions (closer/setter see only own data)
//
// KEY DECISIONS:
// - Domain separation: each concern (transactions, team, client) has its own context
// - Shared org state: organizationId passed as prop to all domain providers
// - Cache invalidation: each domain context exports invalidate*Cache() functions
// - Backward compatibility: existing useData() hook works with new architecture
// - Legacy support: applicants/clients still use N8N webhooks (not refactored yet)
// =============================================================================

// Domain-specific contexts
import {
  TransactionProvider,
  TransactionContext,
  invalidateTransactionCache,
} from "./TransactionContext";
import { TeamProvider, TeamContext, invalidateTeamCache } from "./TeamContext";
import { ClientPortalProvider, ClientPortalContext } from "./ClientPortalContext";

// Instagram DM stats for dashboard metrics (raw records for filtering)
interface InstagramDMStats {
  conversationId: string;
  createdAt: string;
  lastMessageAt: string | null;
  messageCount: number;
  needsResponse: boolean;
}

import { CACHE_VERSION } from "../lib/cacheVersion";

// --- Cache Configuration ---
const CACHE_PREFIX = `altara_${CACHE_VERSION}_data_`;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

// Cache helpers
const getCache = <T,>(key: string, ttl: number, orgId: string): T | null => {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;

    const entry: CacheEntry<T> = JSON.parse(raw);
    const isExpired = Date.now() - entry.timestamp > ttl;
    const isSameOrg = entry.orgId === orgId;

    if (isExpired || !isSameOrg) {
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
};

const setCache = <T,>(key: string, data: T, orgId: string): void => {
  try {
    const entry: CacheEntry<T> = { data, timestamp: Date.now(), orgId };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    logger.warn("Cache write failed");
  }
};

const invalidateOrgCache = (orgId: string): void => {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const entry = JSON.parse(raw);
            if (entry.orgId === orgId) {
              localStorage.removeItem(key);
            }
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
    // Also invalidate domain-specific caches
    invalidateTransactionCache(orgId);
    invalidateTeamCache(orgId);
  } catch {
    // Ignore cleanup errors
  }
};

interface DataContextType {
  // Context State
  currentOrganization: Organization | null;
  availableOrganizations: Organization[];
  isLoadingData: boolean;
  isLoadingPortalData: boolean; // client portal's own tasks/files/comms fetch only
  isLoadingTeamData: boolean; // team/leaderboard fetch only
  switchOrganization: (orgId: string) => void;
  refreshLeaderboard: () => Promise<void>;
  forceRefresh: () => void; // Invalidates cache and refetches all data

  // Domain Data (from domain contexts)
  dailyStats: any[];
  transactions: SalesTransaction[];
  leaderboard: LeaderboardEntry[];
  companyWideLeaderboard: LeaderboardEntry[];
  prestigePoints: PrestigeEntry[];
  prestigeRanks: PrestigeRank[];
  teamMembers: TeamMember[];
  instagramDMStats: InstagramDMStats[];

  // Confidential Data (from ClientPortalContext)
  clientTasks: ClientTask[];
  setClientTasks: React.Dispatch<React.SetStateAction<ClientTask[]>>;
  clientFiles: ClientFile[];
  setClientFiles: React.Dispatch<React.SetStateAction<ClientFile[]>>;
  clientCommunications: ClientCommunication[];
  setClientCommunications: React.Dispatch<React.SetStateAction<ClientCommunication[]>>;

  // Analytics Uploads (from ClientPortalContext)
  analyticsUploads: AnalyticsUpload[];
  loadError: string | null;
  refetch: () => void;
  fetchAnalyticsUploads: (type: "calendly" | "webinar") => Promise<void>;
  saveAnalyticsUpload: (
    fileName: string,
    description: string,
    type: "calendly" | "webinar",
    content: string
  ) => Promise<void>;
  updateAnalyticsUpload: (id: string, fileName: string, description: string) => Promise<void>;
  deleteAnalyticsUpload: (id: string) => Promise<void>;

  // Legacy/Mock Data Support (kept to prevent breakage of other tabs)
  applicants: Applicant[];
  clients: Client[];
  performanceData: DailyPerformanceLog[];
  sharedResources: SharedResource[];
  addApplicant: (applicant: Omit<Applicant, "id" | "applicationDate" | "status">) => void;
  updateApplicant: (applicant: Applicant) => void;
  deleteApplicant: (applicantId: string) => void;
  addClient: (client: Omit<Client, "id" | "applicationDate" | "status">) => void;
  updateClient: (client: Client) => void;
  deleteClient: (clientId: string) => void;
  assignTeamToClient: (clientId: string, teamMemberIds: string[]) => void;
  addSharedResource: (resource: Omit<SharedResource, "id" | "sharedDate" | "sharedBy">) => void;
  setTransactions: (transactions: SalesTransaction[]) => void;
}

export const DataContext = createContext<DataContextType | undefined>(undefined);

// Internal provider that wraps domain contexts
interface DataProviderInternalProps {
  children: ReactNode;
  currentOrg: Organization | null;
  availableOrgs: Organization[];
  onOrgSwitch: (orgId: string) => void;
}

const DataProviderInternal: React.FC<DataProviderInternalProps> = ({
  children,
  currentOrg,
  availableOrgs,
  onOrgSwitch,
}) => {
  const { user } = useAuth();
  const { hasTunnelVision, isCloser, isSetter, canViewGlobalStats } = useRBAC();

  // State: Instagram DM Stats (org-specific data not in domain contexts)
  const [instagramDMStats, setInstagramDMStats] = useState<InstagramDMStats[]>([]);

  // State: Legacy Support (Initialized Empty)
  const [rawApplicants, setRawApplicants] = useState<Applicant[]>([]);
  const [rawClients, setRawClients] = useState<Client[]>([]);
  const [rawPerformanceData] = useState<DailyPerformanceLog[]>([]);
  const [rawSharedResources, setRawSharedResources] = useState<SharedResource[]>([]);

  // Cache control
  const forceRefreshRef = useRef(false);

  // --- Consume domain contexts ---
  const transactionContext = React.useContext(TransactionContext);
  const teamContext = React.useContext(TeamContext);
  const clientPortalContext = React.useContext(ClientPortalContext);

  // Per-domain loading flags. The client portal gates on these instead of the
  // combined isLoadingData: it never reads transactions, and on a wide date range
  // the transactions fetch alone held the whole portal on a skeleton for 20s+.
  const isLoadingPortalData = clientPortalContext?.isLoading ?? false;
  const isLoadingTeamData = teamContext?.isLoading ?? false;

  // Loading state is combined from all domain contexts
  const isLoadingData = useMemo(() => {
    return (
      (transactionContext?.isLoading || teamContext?.isLoading || clientPortalContext?.isLoading) ??
      false
    );
  }, [transactionContext?.isLoading, teamContext?.isLoading, clientPortalContext?.isLoading]);

  // --- Fetch Instagram DM Stats (org-specific data not in domain contexts) ---
  useEffect(() => {
    const fetchInstagramDMStats = async () => {
      if (!currentOrg || !isSupabaseConfigured || !supabase) {
        setInstagramDMStats([]);
        return;
      }

      const orgId = currentOrg.id;
      const shouldForceRefresh = forceRefreshRef.current;

      // Check cache first (unless forcing refresh)
      if (!shouldForceRefresh) {
        const cachedDMStats = getCache<InstagramDMStats[]>("dmStats", CACHE_TTL, orgId);
        if (cachedDMStats) {
          logger.debug("Using cached DM stats");
          setInstagramDMStats(cachedDMStats);
          return;
        }
      }

      try {
        const { data, error } = await supabase
          .from("ghl_conversations")
          .select("id, created_at, last_message_at, message_count, needs_response", {
            count: "exact",
          })
          .eq("organization_id", orgId)
          .eq("is_archived", false)
          .limit(1000); // Safety cap for stats aggregation

        if (error) throw error;

        if (data) {
          interface InstagramDMRow {
            id: string;
            created_at: string;
            last_message_at: string | null;
            message_count: number | null;
            needs_response: boolean | null;
          }
          const mapped: InstagramDMStats[] = (data as InstagramDMRow[]).map((row) => ({
            conversationId: row.id,
            createdAt: row.created_at,
            lastMessageAt: row.last_message_at,
            messageCount: row.message_count || 0,
            needsResponse: row.needs_response || false,
          }));
          setInstagramDMStats(mapped);
          setCache("dmStats", mapped, orgId);
        }
      } catch (err) {
        logger.error("Error fetching Instagram DM stats", err);
      }
    };

    fetchInstagramDMStats();
  }, [currentOrg, forceRefreshRef.current]);

  // --- Actions ---
  const switchOrganization = useCallback(
    (orgId: string) => {
      onOrgSwitch(orgId);
    },
    [onOrgSwitch]
  );

  // Force refresh - invalidates cache and triggers fresh fetch
  const forceRefresh = useCallback(() => {
    if (currentOrg) {
      logger.debug("Force refresh triggered, invalidating cache");
      invalidateOrgCache(currentOrg.id);
      forceRefreshRef.current = true;
      // Trigger a re-render
      onOrgSwitch(currentOrg.id);
      // Reset force refresh flag after a tick
      setTimeout(() => {
        forceRefreshRef.current = false;
      }, 100);
    }
  }, [currentOrg, onOrgSwitch]);

  // --- 4. Filtering (RBAC) ---
  // SECURITY: Filter by ID only - never by name (prevents impersonation)
  const transactions = useMemo(() => {
    if (!user || !transactionContext) return [];
    let filtered = transactionContext.rawTransactions;
    if (hasTunnelVision) {
      if (isCloser) {
        filtered = filtered.filter((t) => t.closerId === user.id);
      } else if (isSetter) {
        filtered = filtered.filter((t) => t.setterId === user.id);
      }
    }
    return filtered;
  }, [transactionContext?.rawTransactions, user, hasTunnelVision, isCloser, isSetter]);

  const clients = useMemo(() => {
    if (!user) return [];
    if (canViewGlobalStats) return rawClients;
    return [];
  }, [rawClients, user, canViewGlobalStats]);

  // --- Legacy Mutators (Fully Wired to N8N) ---

  const addApplicant = useCallback(
    async (d: Omit<Applicant, "id" | "applicationDate" | "status">) => {
      // 1. Update UI Immediately (Optimistic)
      setRawApplicants((p) => [
        {
          ...d,
          id: Date.now().toString(),
          applicationDate: new Date().toISOString(),
          status: ApplicantStatus.Pending,
        },
        ...p,
      ]);
      // 2. Fire PUBLIC Webhook
      try {
        await triggerPublicWebhook(N8N_WEBHOOKS.APPLY_WORKFLOW, {
          action: "create",
          type: "applicant",
          ...d,
        });
      } catch {
        logger.error("Webhook failed");
      }
    },
    []
  );

  const addClient = useCallback(async (d: Omit<Client, "id" | "applicationDate" | "status">) => {
    setRawClients((p) => [
      {
        ...d,
        id: Date.now().toString(),
        applicationDate: new Date().toISOString(),
        status: ClientStatus.Pending,
      },
      ...p,
    ]);
    try {
      await triggerPublicWebhook(N8N_WEBHOOKS.APPLY_WORKFLOW, {
        action: "create",
        type: "client_inquiry",
        ...d,
      });
    } catch {
      logger.error("Webhook failed");
    }
  }, []);

  // Admin Actions -> Securely Trigger N8N
  const updateApplicant = useCallback(async (d: Applicant) => {
    setRawApplicants((p) => p.map((a) => (a.id === d.id ? d : a)));
    await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
      action: "update",
      type: "applicant",
      ...d,
    });
  }, []);

  const deleteApplicant = useCallback(async (id: string) => {
    setRawApplicants((p) => p.filter((a) => a.id !== id));
    await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
      action: "delete",
      type: "applicant",
      id,
    });
  }, []);

  const updateClient = useCallback(async (d: Client) => {
    setRawClients((p) => p.map((c) => (c.id === d.id ? d : c)));
    await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
      action: "update",
      type: "client",
      ...d,
    });
  }, []);

  const deleteClient = useCallback(async (id: string) => {
    setRawClients((p) => p.filter((c) => c.id !== id));
    await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, { action: "delete", type: "client", id });
  }, []);

  const assignTeamToClient = useCallback(async (id: string, t: string[]) => {
    setRawClients((p) =>
      p.map((c) => (c.id === id ? { ...c, assignedTeam: t, status: ClientStatus.Active } : c))
    );
    await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
      action: "assign_team",
      type: "client",
      id,
      assigned_team: t,
    });
  }, []);

  const addSharedResource = useCallback(
    async (d: Omit<SharedResource, "id" | "sharedDate" | "sharedBy">) => {
      const newResource: SharedResource = {
        ...d,
        id: Date.now().toString(),
        sharedDate: new Date().toISOString(),
        sharedBy: user?.id || "system",
      };
      setRawSharedResources((p) => [newResource, ...p]);
      // Extract fields for webhook (excluding 'type' from SharedResource to avoid conflict)
      const { type: resourceType, ...rest } = d;
      await securelyTriggerN8n(N8N_WEBHOOKS.ADMIN_WORKFLOW, {
        action: "create",
        type: "resource",
        resource_type: resourceType,
        ...rest,
      });
    },
    [user]
  );

  // Stable fallbacks — prevent new references on every render
  const emptyArray = useMemo(() => [] as never[], []);
  const noopFn = useCallback(() => {}, []);
  const noopAsyncFn = useCallback(async () => {}, []);

  // Memoize provider value to prevent re-render cascades.
  // Without this, every sub-context change creates a new object reference,
  // forcing ALL consumers (dashboard has 15+ hooks calling useData()) to re-render.
  const contextValue = useMemo(
    () => ({
      currentOrganization: currentOrg,
      availableOrganizations: availableOrgs,
      isLoadingData,
      isLoadingPortalData,
      isLoadingTeamData,
      switchOrganization,
      refreshLeaderboard: teamContext?.refreshLeaderboard ?? noopAsyncFn,
      forceRefresh,
      // Domain data from contexts
      dailyStats: transactionContext?.dailyStats ?? emptyArray,
      transactions,
      leaderboard: teamContext?.leaderboard ?? emptyArray,
      companyWideLeaderboard: teamContext?.companyWideLeaderboard ?? emptyArray,
      prestigePoints: teamContext?.prestigePoints ?? emptyArray,
      prestigeRanks: teamContext?.prestigeRanks ?? emptyArray,
      teamMembers: teamContext?.teamMembers ?? emptyArray,
      instagramDMStats,
      // Confidential Data
      clientTasks: clientPortalContext?.clientTasks ?? emptyArray,
      setClientTasks: clientPortalContext?.setClientTasks ?? noopFn,
      clientFiles: clientPortalContext?.clientFiles ?? emptyArray,
      setClientFiles: clientPortalContext?.setClientFiles ?? noopFn,
      clientCommunications: clientPortalContext?.clientCommunications ?? emptyArray,
      setClientCommunications: clientPortalContext?.setClientCommunications ?? noopFn,
      // Analytics
      analyticsUploads: clientPortalContext?.analyticsUploads ?? emptyArray,
      loadError: clientPortalContext?.loadError ?? null,
      refetch: clientPortalContext?.refetch ?? noopFn,
      fetchAnalyticsUploads: clientPortalContext?.fetchAnalyticsUploads ?? noopAsyncFn,
      saveAnalyticsUpload: clientPortalContext?.saveAnalyticsUpload ?? noopAsyncFn,
      updateAnalyticsUpload: clientPortalContext?.updateAnalyticsUpload ?? noopAsyncFn,
      deleteAnalyticsUpload: clientPortalContext?.deleteAnalyticsUpload ?? noopAsyncFn,
      // Legacy
      applicants: rawApplicants,
      clients,
      performanceData: rawPerformanceData,
      sharedResources: rawSharedResources,
      addApplicant,
      updateApplicant,
      deleteApplicant,
      addClient,
      updateClient,
      deleteClient,
      assignTeamToClient,
      addSharedResource,
      setTransactions: transactionContext?.setTransactions ?? noopFn,
    }),
    [
      currentOrg,
      availableOrgs,
      isLoadingData,
      isLoadingPortalData,
      isLoadingTeamData,
      switchOrganization,
      forceRefresh,
      teamContext?.refreshLeaderboard,
      teamContext?.leaderboard,
      teamContext?.companyWideLeaderboard,
      teamContext?.prestigePoints,
      teamContext?.prestigeRanks,
      teamContext?.teamMembers,
      transactionContext?.dailyStats,
      transactionContext?.setTransactions,
      transactions,
      instagramDMStats,
      clientPortalContext?.clientTasks,
      clientPortalContext?.setClientTasks,
      clientPortalContext?.clientFiles,
      clientPortalContext?.setClientFiles,
      clientPortalContext?.clientCommunications,
      clientPortalContext?.setClientCommunications,
      clientPortalContext?.analyticsUploads,
      clientPortalContext?.fetchAnalyticsUploads,
      clientPortalContext?.loadError,
      clientPortalContext?.refetch,
      clientPortalContext?.saveAnalyticsUpload,
      clientPortalContext?.updateAnalyticsUpload,
      clientPortalContext?.deleteAnalyticsUpload,
      rawApplicants,
      clients,
      rawPerformanceData,
      rawSharedResources,
      addApplicant,
      updateApplicant,
      deleteApplicant,
      addClient,
      updateClient,
      deleteClient,
      assignTeamToClient,
      addSharedResource,
      noopFn,
      noopAsyncFn,
      emptyArray,
    ]
  );

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
};

// Public provider that composes domain contexts
export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, realUser } = useAuth();
  const { showClientPortal } = useRBAC();

  // We need to manage org state here to pass to domain providers
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [forceRefresh] = useState(false);

  // Fetch organizations (duplicated from internal provider to avoid circular dependency)
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!realUser || !supabase) return;

      try {
        const { data, error } = await supabase
          .from("organizations")
          .select("id, name, slug, status, timezone, settings")
          .order("name")
          .limit(100);

        if (error) throw error;

        let orgs = (data as Organization[]) || [];
        const isGlobalAdmin = realUser.role === "exec";

        if (!isGlobalAdmin) {
          const allowedIds = Object.keys(realUser.orgRoles || {});
          orgs = orgs.filter((o) => allowedIds.includes(o.id));
        }

        setAvailableOrganizations(orgs);

        if (orgs.length > 0) {
          if (!currentOrganization) {
            const persistedId = localStorage.getItem("altara_last_org_id");
            // Default to Rayston if no persisted org
            const rayston = orgs.find((o) => o.name?.toLowerCase().includes("rayston"));
            const target = orgs.find((o) => o.id === persistedId) || rayston || orgs[0];
            setCurrentOrganization(target);
          } else if (!orgs.find((o) => o.id === currentOrganization.id)) {
            setCurrentOrganization(orgs[0]);
            localStorage.setItem("altara_last_org_id", orgs[0].id);
          }
        } else {
          setCurrentOrganization(null);
        }
      } catch {
        logger.error("Failed to fetch organizations");
      }
    };

    fetchOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realUser?.id, realUser?.role]);

  // Share org state between domain contexts
  const orgId = currentOrganization?.id ?? null;
  // Stabilize availableOrgIds: only create a new array reference when the
  // actual IDs change, not when availableOrganizations gets a new array
  // with the same content (e.g. from a redundant fetchOrganizations call).
  const prevOrgIdsRef = useRef<string[]>([]);
  const availableOrgIds = useMemo(() => {
    const ids = availableOrganizations.map((o) => o.id);
    const prev = prevOrgIdsRef.current;
    if (ids.length === prev.length && ids.every((id, i) => id === prev[i])) {
      return prev; // same IDs — return stable reference
    }
    prevOrgIdsRef.current = ids;
    return ids;
  }, [availableOrganizations]);

  const handleOrgSwitch = useCallback(
    (orgId: string) => {
      const org = availableOrganizations.find((o) => o.id === orgId);
      if (org) {
        setCurrentOrganization(org);
        localStorage.setItem("altara_last_org_id", orgId);
      }
    },
    [availableOrganizations]
  );

  return (
    <TransactionProvider
      organizationId={orgId}
      orgTimezone={currentOrganization?.timezone ?? null}
      forceRefresh={forceRefresh}
    >
      <TeamProvider
        organizationId={orgId}
        availableOrgIds={availableOrgIds}
        forceRefresh={forceRefresh}
      >
        <ClientPortalProvider
          organizationId={orgId}
          userId={user?.id ?? null}
          userRole={user?.role ?? null}
          canViewClientPortal={showClientPortal}
        >
          <DataProviderInternal
            currentOrg={currentOrganization}
            availableOrgs={availableOrganizations}
            onOrgSwitch={handleOrgSwitch}
          >
            {children}
          </DataProviderInternal>
        </ClientPortalProvider>
      </TeamProvider>
    </TransactionProvider>
  );
};
